import React, { useCallback, useMemo } from 'react';
import { Box, Toggle, Flex, TextV2 } from '@withjoy/joykit';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { FormikProps } from 'formik';
import { AddCashFundDialogFields } from '../../../../types';
import { useHistory } from '@shared/core';
import { useLocation } from '@react-router';
import { useCreditCardSetupTelemetry } from '@apps/registry/admin/components/CreditCardSetup/CreditCardSetUp.telemetry';
import { styles } from '../../CashFundPaymentMethod.style';
import { ReactComponent as ApplePayIcon } from '@assets/icons/apple-pay.svg';
import { ReactComponent as GooglePayIcon } from '@assets/icons/google-pay.svg';
import { pxToRem } from '@withjoy/joykit/theme';

type CreditCardToggleProps = Readonly<{
  formik: FormikProps<AddCashFundDialogFields>;
  canEnableCreditCard: boolean;
}>;

export const CreditCardToggle: React.FC<CreditCardToggleProps> = props => {
  const { canEnableCreditCard } = props;
  const location = useLocation();
  const history = useHistory();
  const telemetry = useCreditCardSetupTelemetry();
  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const { creditCardOption, recommended, creditCardOptionNote, fastCheckoutWith } = getCashFundDialogTranslations();
  const setUpCreditCardLocation = useMemo(
    () => (location.search ? `${location.pathname}${location.search}&setupCreditCard=true` : `${location.pathname}?setupCreditCard=true`),
    [location],
  );

  const onToggleChange = useCallback(
    e => {
      if (canEnableCreditCard) {
        return props.formik.handleChange(e);
      }
      telemetry.toggleCreditCardOption();
      return history.push(setUpCreditCardLocation);
    },
    [canEnableCreditCard, history, setUpCreditCardLocation, props.formik, telemetry],
  );

  return (
    <Box marginTop={5}>
      <Flex __css={styles.toggleWrapper({ isFeatured: !props.formik.values.enableJoyCredit })}>
        <Flex
          flexDirection="column"
          gap={2}
        >
          <Flex alignItems="center">
            <TextV2
              typographyVariant="hed1"
              marginRight={3}
              whiteSpace={'nowrap'}
            >
              {creditCardOption}
            </TextV2>
            {!props.formik.values.enableJoyCredit && (
              <Box
                border="1px solid"
                borderColor="#4751F8"
                borderRadius="4px"
                paddingX={1}
              >
                <TextV2
                  typographyVariant="label3"
                  color="#4751F8"
                >
                  {recommended}
                </TextV2>
              </Box>
            )}
          </Flex>
          <TextV2
            typographyVariant="label1"
            color="mono8"
          >
            {creditCardOptionNote}
          </TextV2>
          <Flex alignItems="center">
            <TextV2
              typographyVariant="label1"
              color="mono14"
              marginRight={2}
            >
              {fastCheckoutWith}
            </TextV2>
            <Box marginRight={pxToRem(5)}>
              <ApplePayIcon />
            </Box>
            <Box>
              <GooglePayIcon />
            </Box>
          </Flex>
        </Flex>
        <Flex justifyContent="center">
          <Toggle
            label=""
            intent="primary"
            hideLabel={true}
            checked={props.formik.values.enableJoyCredit}
            name="enableJoyCredit"
            onChange={onToggleChange}
          />
        </Flex>
      </Flex>
    </Box>
  );
};
